import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, FactGrid, Callout, MiniLabel, Panel } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Where This Fits: Regulated Finance & Interoperability',
    tagline: 'Privacy, authorization, and interoperability: together, on purpose.',
};

type Support = 'yes' | 'partial' | 'no';

const ARCHITECTURES = ['Public chain', 'Private databases', 'Consortium chain', 'Canton'];

const REQUIREMENTS: { id: string; label: string; support: Support[] }[] = [
    { id: 'confidential', label: 'Confidential between counterparties', support: ['no', 'yes', 'partial', 'yes'] },
    { id: 'shared-truth', label: 'Shared source of truth, no reconciliation', support: ['yes', 'no', 'yes', 'yes'] },
    { id: 'authorization', label: 'Multi-party authorization enforced by the ledger', support: ['partial', 'no', 'partial', 'yes'] },
    { id: 'atomic', label: 'Atomic settlement across institutions', support: ['yes', 'no', 'yes', 'yes'] },
    { id: 'interop', label: 'Interoperates across separately run networks', support: ['partial', 'no', 'no', 'yes'] },
    { id: 'global-audit', label: 'Anyone can independently verify all history', support: ['yes', 'no', 'partial', 'no'] },
    { id: 'permissionless', label: 'Anyone can join without being onboarded', support: ['yes', 'no', 'no', 'partial'] },
];

const RequirementPicker = () => {
    const colors = useColors();
    const [picked, setPicked] = useState<string[]>(['confidential', 'shared-truth', 'authorization', 'interop']);
    const pickedRows = REQUIREMENTS.filter((row) => picked.includes(row.id));
    const fits = ARCHITECTURES.map((_, column) => pickedRows.every((row) => row.support[column] === 'yes'));
    const winners = ARCHITECTURES.filter((_, column) => fits[column]);

    const symbol = (support: Support) => ({
        yes: { mark: '✓', color: colors.accent.green, label: 'yes' },
        partial: { mark: '~', color: colors.difficulty.medium, label: 'partially' },
        no: { mark: '✗', color: colors.text.muted, label: 'no' },
    })[support];

    const toggle = (id: string) => setPicked((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
    const cell = { p: '8px 10px', borderBottom: `1px solid ${colors.border.subtle}`, textAlign: 'center' as const };

    return (
        <Panel>
            <MiniLabel>Tick the requirements your use case has</MiniLabel>
            <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                    <thead>
                        <tr>
                            <Box component="th" sx={{ ...cell, textAlign: 'left' }} />
                            {ARCHITECTURES.map((name, column) => (
                                <Box component="th" key={name} sx={{
                                    ...cell,
                                    color: fits[column] ? colors.accent.cyan : colors.text.secondary,
                                    backgroundColor: fits[column] ? colors.accent.cyanGlow : 'transparent',
                                    fontSize: '0.8rem',
                                    fontWeight: 800,
                                    transition: 'all 0.25s ease',
                                }}>
                                    {name}
                                </Box>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {REQUIREMENTS.map((row) => {
                            const on = picked.includes(row.id);
                            return (
                                <Box component="tr" key={row.id} onClick={() => toggle(row.id)} sx={{ cursor: 'pointer', opacity: on ? 1 : 0.5, transition: 'opacity 0.2s ease' }}>
                                    <Box component="td" sx={{ ...cell, textAlign: 'left' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                size="small"
                                                checked={on}
                                                onClick={(event) => event.stopPropagation()}
                                                onChange={() => toggle(row.id)}
                                                inputProps={{ 'aria-label': row.label }}
                                                sx={{ color: colors.border.strong, '&.Mui-checked': { color: colors.accent.cyan }, p: '4px', mr: 0.5 }}
                                            />
                                            <Typography sx={{ color: colors.text.primary, fontSize: '0.86rem' }}>{row.label}</Typography>
                                        </Box>
                                    </Box>
                                    {row.support.map((support, column) => {
                                        const item = symbol(support);
                                        return (
                                            <Box component="td" key={column} sx={{
                                                ...cell,
                                                color: item.color,
                                                fontWeight: 800,
                                                backgroundColor: fits[column] ? colors.accent.cyanGlow : 'transparent',
                                            }}>
                                                <span aria-label={item.label}>{item.mark}</span>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            );
                        })}
                    </tbody>
                </Box>
            </Box>
            <Typography sx={{ color: colors.text.secondary, fontSize: '0.9rem', lineHeight: 1.65, mt: 2 }} aria-live="polite">
                {pickedRows.length === 0
                    ? 'Tick at least one requirement.'
                    : winners.length === 0
                        ? 'No architecture meets every ticked requirement in full. Some of these properties pull against each other: that tension is the whole design space.'
                        : <>Fully meets all {pickedRows.length} ticked requirements: <strong style={{ color: colors.text.primary }}>{winners.join(', ')}</strong>.</>}
            </Typography>
        </Panel>
    );
};

const TwoPlanes = () => {
    const colors = useColors();
    const plane = (title: string, who: string, items: string[]) => (
        <Box sx={{ p: '16px', borderRadius: '8px', border: `1px solid ${colors.border.default}`, backgroundColor: colors.bg.primary }}>
            <Typography sx={{ color: colors.accent.cyan, fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{title}</Typography>
            <Typography sx={{ color: colors.text.primary, fontWeight: 800, mb: 1 }}>{who}</Typography>
            <Box component="ul" sx={{ color: colors.text.secondary, fontSize: '0.88rem', lineHeight: 1.6, pl: '18px', m: 0 }}>
                {items.map((item) => <li key={item}>{item}</li>)}
            </Box>
        </Box>
    );
    return (
        <Box sx={{ my: 2.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '14px' }}>
                {plane('Who sees what', 'Participant nodes', [
                    'Receive only the views their parties are informees of',
                    'Decrypt and re-execute those views with DAML',
                    'Approve or reject on their parties\' behalf',
                ])}
                {plane('Who agrees on order', 'Synchronizer', [
                    'Sequencer: one timestamped order of sealed messages',
                    'Mediator: collects approvals, announces the verdict',
                    'Never reads a single contract',
                ])}
            </Box>
            <Box sx={{ mt: '14px', p: '14px 16px', borderRadius: '8px', border: `1px solid ${colors.border.accent}`, backgroundColor: colors.accent.cyanGlow, textAlign: 'center' }}>
                <Typography sx={{ color: colors.text.primary, fontWeight: 800 }}>
                    Consensus = every stakeholder approved its own view + everyone saw the same order
                </Typography>
            </Box>
        </Box>
    );
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - the combination of properties Canton is specifically designed to provide together
//   Q1 - how Canton achieves consensus while preserving privacy (separating visibility from ordering)
export const blocks: StageBlock[] = [
    content('closing', () => (
        <StageSection gradientBorder eyebrow="Stage 8 of 8" title="Putting the pieces together">
            <Prose>
                Tokenized bonds and funds, repo and collateral management, cross-border payments, trade settlement:
                these workflows all need the same combination of properties at once. They need{' '}
                <strong>confidentiality</strong> between competitors, <strong>explicit multi-party
                authorization</strong> so nobody is bound without consent, <strong>atomic settlement</strong> so no
                one is left holding half a trade, and <strong>interoperability</strong> across institutions that
                don't run identical infrastructure.
            </Prose>
            <Prose>
                Each familiar architecture gives up something. A fully public chain gives up confidentiality. Private
                databases give up the shared source of truth, and reconciliation fills the gap. A consortium chain
                hides data from outsiders but not from its own members, and doesn't talk to other consortia. Canton
                was designed to provide all of those properties together, and it pays for that with properties public
                chains have and it doesn't. Tick requirements below to see the trade-offs.
            </Prose>
            <RequirementPicker />
            <Prose>
                That last point matters. Canton isn't "a better Ethereum": it deliberately gives up the property that
                anyone can audit everything, and participation typically involves onboarding. For regulated
                institutions that's usually a requirement, not a drawback. The public <strong>Canton Network</strong>{' '}
                ties these institutions together through the Global Synchronizer, whose usage is paid for with its
                native token, Canton Coin.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('recap', () => (
        <StageSection title="The core trick, restated">
            <Prose>
                Everything in this track reduces to one separation. <strong>Who sees what</strong> (each participant
                sees only the contracts relevant to the parties it hosts) is kept independent from{' '}
                <strong>who agrees on ordering</strong> (the synchronizer, which sequences transactions without ever
                reading them). A public blockchain welds the two together: to agree on order, every node must see
                and execute everything. Canton pulls them apart.
            </Prose>
            <TwoPlanes />
            <Prose>
                Put together, consensus in Canton doesn't come from miners or stakers voting on a global state. It
                comes from the stakeholders of each transaction validating and approving their own parts, with the
                synchronizer providing a single order so their approvals refer to the same history. That's the
                "proof of stakeholder" idea from Stage 3, and it's how Canton gets consistency and privacy at the
                same time.
            </Prose>
            <MiniLabel>Track cheat sheet</MiniLabel>
            <FactGrid columns={3} items={[
                ['Party', 'A named identity, hosted on participant nodes.'],
                ['Contract', 'An immutable agreement; active until archived.'],
                ['Signatory', 'Must authorize creation; bound by the contract.'],
                ['Observer', 'Sees the contract; bound by nothing.'],
                ['Choice / controller', 'The only ways to act on a contract, and who may.'],
                ['Participant node', 'Stores and validates its parties\' slice of the ledger.'],
                ['Synchronizer', 'Sequencer + mediator: orders and coordinates, never reads.'],
                ['Sub-transaction privacy', 'Each party sees only its projection of a transaction.'],
                ['Reassignment', 'Moves contracts between synchronizers for composability.'],
            ]} />
            <Callout tone="security" title="Where the security tracks go next">
                Every mechanism in this track is also an attack surface. Authorization bugs come from wrong
                signatories and controllers, and from authority leaking through choices. Privacy bugs come from
                observers and fetches that disclose more than intended. State-integrity bugs come from wrong
                consumption, stale contract IDs and unchecked invariants. And there are trust assumptions about
                nodes and synchronizers. The Foundation, Advanced and DAML security tracks take each of these apart
                with real code.
            </Callout>
        </StageSection>
    )),

    question(1),
];
